import { Injectable, signal } from '@angular/core';

export type DialogType = 'alert' | 'confirm' | 'prompt';

interface DialogState {
  visible: boolean;
  type: DialogType;
  title?: string;
  message: string;
  promptValue: string;
  promptPlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
}

const INITIAL_STATE: DialogState = {
  visible: false,
  type: 'alert',
  title: undefined,
  message: '',
  promptValue: '',
  promptPlaceholder: '',
  confirmLabel: 'Aceptar',
  cancelLabel: 'Cancelar',
  danger: false,
};

export interface AlertOptions {
  title?: string;
  confirmLabel?: string;
}

export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Pinta el botón de confirmar en rojo, para acciones destructivas (eliminar, etc). */
  danger?: boolean;
}

export interface PromptOptions {
  title?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * Reemplazo para window.alert / window.confirm / window.prompt.
 *
 * Estos diálogos nativos bloquean el hilo del navegador, no se pueden
 * estilizar y no respetan dark mode. Este servicio expone la misma forma de
 * uso (await this.dialog.confirm('¿Seguro?')) pero renderiza un modal propio
 * (ver DialogComponent) montado una sola vez en el root de la app.
 *
 * El estado vive en un signal (`state`) que DialogComponent lee
 * directamente; como la app corre sin zone.js, un signal es lo único que
 * garantiza que la vista se actualice al abrir/cerrar el modal.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  readonly state = signal<DialogState>(INITIAL_STATE);

  private resolver: ((value: unknown) => void) | null = null;

  /** Sustituye a window.alert(message). Se resuelve cuando el usuario cierra el modal. */
  alert(message: string, options: AlertOptions = {}): Promise<void> {
    return new Promise<void>((resolve) => {
      this.open(resolve, {
        ...INITIAL_STATE,
        visible: true,
        type: 'alert',
        message,
        title: options.title,
        confirmLabel: options.confirmLabel || 'Aceptar',
      });
    });
  }

  /** Sustituye a window.confirm(message). Resuelve true/false según el botón que se use. */
  confirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.open(resolve, {
        ...INITIAL_STATE,
        visible: true,
        type: 'confirm',
        message,
        title: options.title,
        confirmLabel: options.confirmLabel || 'Confirmar',
        cancelLabel: options.cancelLabel || 'Cancelar',
        danger: options.danger ?? false,
      });
    });
  }

  /** Sustituye a window.prompt(message). Resuelve el texto ingresado, o null si se cancela. */
  prompt(message: string, options: PromptOptions = {}): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      this.open(resolve, {
        ...INITIAL_STATE,
        visible: true,
        type: 'prompt',
        message,
        title: options.title,
        promptValue: options.defaultValue || '',
        promptPlaceholder: options.placeholder || '',
        confirmLabel: options.confirmLabel || 'Aceptar',
        cancelLabel: options.cancelLabel || 'Cancelar',
      });
    });
  }

  /** Usado por DialogComponent para reflejar lo que el usuario escribe en el input de prompt(). */
  updatePromptValue(value: string): void {
    this.state.update((current) => ({ ...current, promptValue: value }));
  }

  /** Usado por DialogComponent al pulsar confirmar/cancelar/backdrop/Escape. */
  respond(value: unknown): void {
    const resolve = this.resolver;
    this.resolver = null;
    this.state.set(INITIAL_STATE);
    resolve?.(value);
  }

  private open(resolve: (value: any) => void, next: DialogState): void {
    // Si ya hay un diálogo abierto (no debería pasar en uso normal), lo
    // resolvemos como cancelado antes de abrir el nuevo para no dejar
    // promesas colgadas.
    if (this.resolver) {
      this.respond(false);
    }
    this.resolver = resolve;
    this.state.set(next);
  }
}
